import React, { useState, useMemo } from "react";
import {
  Shield,
  DollarSign,
  Stethoscope,
  Baby,
  FileText,
  Scale,
  Info,
  ChevronRight,
  AlertCircle,
  Phone,
  CheckCircle2,
  BookOpen,
  MapPin,
  Clock,
  Briefcase,
  Search,
  Hammer,
  GraduationCap,
  Bell,
  Heart,
  ChevronDown,
  ArrowRight,
  Calculator,
  UserCheck,
  HelpCircle,
  X,
  ExternalLink,
  Link as LinkIcon,
  Lightbulb,
  Monitor,
  Brain,
  Users,
  TrendingUp,
  Gavel,
  Library,
  Target,
  Umbrella,
  Zap,
  HandHelping,
  FileWarning,
  BadgeCheck,
  Home,
} from "lucide-react";

const App = () => {
  const [activeSection, setActiveSection] = useState("dashboard");
  const [salaryConfig, setSalaryConfig] = useState({
    category: "BSN",
    exp: "yes",
  });
  const [benefitHours, setBenefitHours] = useState(130);
  const [searchQuery, setSearchQuery] = useState("");
  const [faqCategory, setFaqCategory] = useState("Todos");

  // --- Data: Scopes of Practice (Alcance Detallado por Ley 254) ---
  const nursingCategories = {
    LPN: {
      title: "Enfermera(o) Práctica(o) Licenciada(o) (LPN)",
      base: 1750,
      exp: 2000,
      scope:
        "Ejecuta cuidados de salud selectivos que no requieren los conocimientos complejos de un RN. Su práctica es DEPENDIENTE y siempre bajo supervisión.",
      details: [
        "Participa en la planificación del cuidado bajo guía de un RN.",
        "Venopunción y flebotomía SOLO bajo supervisión directa (RN presente).",
        "Documenta observaciones objetivas en el récord.",
        "Prohibido realizar valoraciones iniciales o diagnósticos independientes.",
        "No puede delegar tareas a personal no licenciado.",
      ],
      warning:
        "Realizar procedimientos invasivos sin el RN en la unidad es práctica ilegal.",
    },
    ADN: {
      title: "Enfermera(o) Asociada(o) (ADN)",
      base: 2250,
      exp: 2500,
      scope:
        "Colabora en el cuidado directo en escenarios de salud estructurados. Posee conocimientos técnicos para ejecutar planes de cuidado.",
      details: [
        "Ejecuta medidas terapéuticas y farmacológicas prescritas.",
        "Colabora en estimados clínicos y recolección de data.",
        "Puede supervisar a LPNs en tareas técnicas específicas.",
        "Prohibido por ley ocupar puestos de alta jerarquía administrativa.",
        "Requiere validación de intervenciones complejas por un Generalista.",
      ],
      warning:
        "Asumir cargos de supervisor general siendo ADN expone tu licencia a responsabilidad civil.",
    },
    BSN: {
      title: "Enfermera(o) Generalista (BSN)",
      base: 2750,
      exp: 3000,
      scope:
        "Responsable de la gestión, planificación, ejecución y evaluación del cuidado. Posee autonomía legal plena para la práctica independiente.",
      details: [
        "Realiza estimados y diagnósticos de enfermería autónomos.",
        "Delega tareas legalmente a ADNs, LPNs y personal de apoyo (UAPs).",
        "Facultad por ley para establecer oficina de servicios privados.",
        "Responsable de la coordinación interdisciplinaria del cuidado.",
        "Lidera la educación autónoma al paciente y comunidad.",
      ],
      warning:
        "Eres legalmente responsable de la supervisión de la competencia del personal a quien delegas.",
    },
    MSN: {
      title: "Enfermera(o) Especialista (MSN)",
      base: 3500,
      exp: 4000,
      scope:
        "Líder clínico con peritaje avanzado en un área. Funciona como consultor, educador e investigador dentro del sistema.",
      details: [
        "Provee consultoría experta en casos clínicos complejos.",
        "Lidera programas de mejora de calidad y seguridad institucional.",
        "Maneja protocolos avanzados en áreas críticas de especialidad.",
        "Evalúa la efectividad de las guías de práctica clínica.",
        "Certifica competencias técnicas de niveles inferiores.",
      ],
      warning:
        "Tu estándar de cuidado en litigios es superior debido a tu grado de especialidad.",
    },
    APRN: {
      title: "Práctica Avanzada (NP, CNS, CRNA, CNM)",
      base: 4500,
      exp: 5500,
      scope:
        "Profesionales con facultad de diagnóstico clínico y prescripción mediante acuerdos de práctica colaborativa con médicos.",
      details: [
        "Ordena e interpreta pruebas diagnósticas y de laboratorio.",
        "Prescribe medicamentos y tratamientos bajo contrato de colaboración.",
        "Realiza procedimientos clínicos avanzados de su especialidad.",
        "Maneja casos de forma independiente en clínicas y hospitales.",
        "Certifica condiciones de salud para propósitos legales.",
      ],
      warning:
        "Prescribir sin un contrato de colaboración vigente y radicado es causa de revocación inmediata.",
    },
  };

  // --- Data: Compendio Legal Expandido (23 Leyes) ---
  const lawsCompendium = [
    {
      id: "254",
      title: "Ley Núm. 254-2015",
      subtitle: "Ley de Enfermería",
      desc: "Regula categorías, licencias y alcances de práctica.",
      impact: "Es la base de tu defensa ante cargos de negligencia.",
    },
    {
      id: "82",
      title: "Ley Núm. 82-1973",
      subtitle: "Ley del Colegio",
      desc: "Establece colegiación obligatoria en PR.",
      impact: "Garantiza representación gremial y seguros básicos.",
    },
    {
      id: "137",
      title: "Ley Núm. 137-2020",
      subtitle: "Salario Mínimo Privado",
      desc: "Establece escalas salariales por grado y experiencia.",
      impact: "Herramienta legal para exigir sueldos justos.",
    },
    {
      id: "115",
      title: "Ley Núm. 115-1991",
      subtitle: "Ley contra Represalias",
      desc: "Protege al 'whistleblower' o denunciante de irregularidades.",
      impact: "Si denuncias ratios peligrosos, no te pueden despedir.",
    },
    {
      id: "87",
      title: "Ley Núm. 87-2025",
      subtitle: "Código de Lactancia",
      desc: "Elimina certificados médicos, garantiza 1h paga por 12 meses.",
      impact: "Protección absoluta a la salud de la madre y el infante.",
    },
    {
      id: "80",
      title: "Ley Núm. 80-1976",
      subtitle: "Despido Injustificado",
      desc: "Obliga al pago de mesada por despido caprichoso.",
      impact: "Estabilidad económica ante cambios patronales arbitrarios.",
    },
    {
      id: "60",
      title: "Ley Núm. 60-2018",
      subtitle: "Enfermedad Familiares",
      desc: "Permite usar 5 días de enfermedad para cuidar familiares.",
      impact: "Reconocimiento legal del balance vida-trabajo.",
    },
    {
      id: "139",
      title: "Ley Núm. 139-1976",
      subtitle: "Buen Samaritano",
      desc: "Inmunidad civil en emergencias voluntarias externas.",
      impact: "Te protege si prestas auxilio en accidentes de calle.",
    },
    {
      id: "379",
      title: "Ley Núm. 379-1948",
      subtitle: "Jornada de Trabajo",
      desc: "Regula horas extras y descansos obligatorios.",
      impact: "Asegura pago de tiempo y medio tras las 8 horas.",
    },
    {
      id: "194",
      title: "Ley Núm. 194-2000",
      subtitle: "Carta Derechos Paciente",
      desc: "Derecho a servicios dignos, privacidad y consentimiento.",
      impact: "El enfermero es el principal garante de estos derechos.",
    },
    {
      id: "11",
      title: "Ley Núm. 11-2001",
      subtitle: "Procurador del Paciente",
      desc: "Entidad que fiscaliza la calidad de los servicios de salud.",
      impact: "Agencia ante la cual se denuncian faltas institucionales.",
    },
    {
      id: "300",
      title: "Ley Núm. 300-1999",
      subtitle: "Historial Delictivo",
      desc: "Cotejo obligatorio para personal de salud.",
      impact: "Requisito para proteger menores y adultos mayores.",
    },
    {
      id: "121",
      title: "Ley Núm. 121-2019",
      subtitle: "Derechos Edad Avanzada",
      desc: "Protección integral a la población de edad avanzada.",
      impact: "Marco legal para enfermeros en hogares y hospicios.",
    },
    {
      id: "17",
      title: "Ley Núm. 17-1988",
      subtitle: "Hostigamiento Sexual",
      desc: "Prohíbe y castiga el acoso en el escenario de empleo.",
      impact: "Mecanismo de defensa ante conducta impropia.",
    },
    {
      id: "44",
      title: "Ley Núm. 44-1985",
      subtitle: "Discrimen Impedimentos",
      desc: "Exige acomodo razonable para empleados con condiciones.",
      impact: "Protección si el enfermero sufre una condición crónica.",
    },
    {
      id: "100",
      title: "Ley Núm. 100-1959",
      subtitle: "Antidiscrimen",
      desc: "Prohíbe trato desigual por edad, sexo o raza.",
      impact: "Base para demandas por trato discriminatorio en el turno.",
    },
    {
      id: "54",
      title: "Ley Núm. 54-1989",
      subtitle: "Violencia Doméstica",
      desc: "Protocolos obligatorios de intervención en salud.",
      impact: "Responsabilidad legal de documentar y referir sospechas.",
    },
    {
      id: "136",
      title: "Ley Núm. 136-2020",
      subtitle: "Salarios Públicos",
      desc: "Escalas salariales para enfermeros del gobierno.",
      impact: "Asegura sueldos competitivos en agencias estatales.",
    },
    {
      id: "427",
      title: "Ley Núm. 427-2000",
      subtitle: "Lactancia Original",
      desc: "Ley previa sobre periodos de extracción de leche.",
      impact: "Base histórica de las protecciones vigentes.",
    },
    {
      id: "4",
      title: "Ley Núm. 4-2017",
      subtitle: "Transformación Laboral",
      desc: "Estableció probatorias de 9 meses y regla de 130h.",
      impact: "Define cómo generas tus vacaciones y enfermedad hoy.",
    },
    {
      id: "281",
      title: "Ley Núm. 281-2003",
      subtitle: "Enfermería Práctica",
      desc: "Regula específicamente al personal LPN.",
      impact: "Define la supervisión mandatoria de este nivel.",
    },
    {
      id: "133",
      title: "Ley Núm. 133-1999",
      subtitle: "Seguridad Infantes",
      desc: "Protección contra robo de infantes en hospitales.",
      impact: "Responsabilidad legal en NICU y Maternidad.",
    },
    {
      id: "160",
      title: "Ley Núm. 160-2001",
      subtitle: "Tratamiento Terminal",
      desc: "Directrices anticipadas y testamento vital.",
      impact: "Obliga a respetar la voluntad del paciente en fin de vida.",
    },
  ];

  // --- Data: FAQs Masivos (20+ por tópico) ---
  const faqData = [
    // LICENCIA (20+)
    {
      q: "¿Vigencia de mi licencia?",
      a: "Tres años naturales a partir de la fecha de expedición.",
      cat: "Licencia",
    },
    {
      q: "¿Curso de Dengue obligatorio?",
      a: "Sí, 1h para renovar entre 2025-2028 según la Resolución 2024-159.",
      cat: "Licencia",
    },
    {
      q: "¿Cuánto cuesta la revalida?",
      a: "$50.00 por solicitud de examen o licencia provisional.",
      cat: "Licencia",
    },
    {
      q: "¿Créditos para BSN?",
      a: "30 horas por trienio de renovación.",
      cat: "Licencia",
    },
    {
      q: "¿Créditos para APRN?",
      a: "50 horas por trienio, con 20 en su especialidad clínica.",
      cat: "Licencia",
    },
    {
      q: "¿Costo de Ley 300?",
      a: "$70.00 pagaderos al Departamento de Justicia.",
      cat: "Licencia",
    },
    {
      q: "¿Se aceptan créditos online?",
      a: "Sí, sin límite de cantidad bajo el Reglamento 9651 de 2024.",
      cat: "Licencia",
    },
    {
      q: "¿ID para el NCLEX?",
      a: "Pasaporte vigente es obligatorio en los centros Pearson de PR.",
      cat: "Licencia",
    },
    {
      q: "¿Inactivar mi licencia?",
      a: "Sí, puedes radicar formulario oficial si no piensas ejercer por un tiempo.",
      cat: "Licencia",
    },
    {
      q: "¿Reglamento de Telesalud?",
      a: "Reglamento 9518; requiere certificación y 4h de educación extra.",
      cat: "Licencia",
    },
    {
      q: "¿Cambio ADN a BSN?",
      a: "Debes radicar la transcripción de crédito y pagar $50.00.",
      cat: "Licencia",
    },
    {
      q: "¿Qué es el rapback?",
      a: "Sistema de cotejo continuo de historial delictivo del Dept. Justicia.",
      cat: "Licencia",
    },
    {
      q: "¿Costo del Good Standing?",
      a: "$30.00; prueba que no tienes querellas éticas pendientes.",
      cat: "Licencia",
    },
    {
      q: "¿Renovar si vivo fuera?",
      a: "Sí, pero 50% de los créditos deben ser de proveedores de PR.",
      cat: "Licencia",
    },
    {
      q: "¿Dengue gratis?",
      a: "Disponible en el portal oficial del Departamento de Salud.",
      cat: "Licencia",
    },
    {
      q: "¿Recargo por renovación tarde?",
      a: "Varia según meses de mora; requiere créditos de educación extra.",
      cat: "Licencia",
    },
    {
      q: "¿Licencia provisional?",
      a: "Válida por 1 año para recién graduados en espera de revalida.",
      cat: "Licencia",
    },
    {
      q: "¿Duplicado de licencia?",
      a: "$30.00 solicitados a través del portal ORCPS.",
      cat: "Licencia",
    },
    {
      q: "¿ASUME y licencia?",
      a: "La Junta requiere certificación negativa para renovar tu licencia.",
      cat: "Licencia",
    },
    {
      q: "¿Pago de cuota Colegio?",
      a: "Requisito legal previo para tramitar renovación ante la Junta.",
      cat: "Licencia",
    },

    // PRÁCTICA (20+)
    {
      q: "¿Ley Buen Samaritano?",
      a: "Ley 139-1976. Inmunidad civil por ayuda voluntaria en emergencias.",
      cat: "Práctica",
    },
    {
      q: "¿Abandono de paciente?",
      a: "Delito ético de irse sin entregar el turno a personal cualificado.",
      cat: "Práctica",
    },
    {
      q: "¿Tareas de limpieza?",
      a: "Canon 13: No debes aceptar tareas contra estándares de la profesión.",
      cat: "Práctica",
    },
    {
      q: "¿HIPAA en PR?",
      a: "Ley federal mandatoria para proteger la privacidad de la data del paciente.",
      cat: "Práctica",
    },
    {
      q: "¿Seguridad infantes?",
      a: "Ley 133 de 1999 establece protocolos contra robo de bebés.",
      cat: "Práctica",
    },
    {
      q: "¿Reporte de maltrato?",
      a: "Obligación legal inmediata ante sospecha en niños o adultos mayores.",
      cat: "Práctica",
    },
    {
      q: "¿Consentimiento informado?",
      a: "Tu rol es validar que el paciente comprendió antes de firmar.",
      cat: "Práctica",
    },
    {
      q: "¿LPN canaliza solo?",
      a: "Ilegal. Requiere supervisión directa física de un RN o Médico.",
      cat: "Práctica",
    },
    {
      q: "¿ADN supervisor?",
      a: "Ilegal. Puestos de alta jerarquía administrativa requieren BSN o superior.",
      cat: "Práctica",
    },
    {
      q: "¿Práctica colaborativa?",
      a: "Contrato legal obligatorio para que APRN pueda recetar.",
      cat: "Práctica",
    },
    {
      q: "¿Negativa ética?",
      a: "Puedes rehusar un tratamiento por conciencia, pero debes referir.",
      cat: "Práctica",
    },
    {
      q: "¿Testamento vital?",
      a: "Ley 160-2001 sobre voluntad del paciente en etapa terminal.",
      cat: "Práctica",
    },
    {
      q: "¿Segunda opinión?",
      a: "Derecho constitucional del paciente garantizado por el Canon 10.",
      cat: "Práctica",
    },
    {
      q: "¿Carta de Derechos?",
      a: "Ley 194-2000 garantiza trato digno y servicios de calidad.",
      cat: "Práctica",
    },
    {
      q: "¿Récord médico patrono?",
      a: "El patrono no tiene acceso sin autorización expresa del empleado.",
      cat: "Práctica",
    },
    {
      q: "¿Impericia profesional?",
      a: "Cualquier daño causado por omisión del estándar de cuidado razonable.",
      cat: "Práctica",
    },
    {
      q: "¿Bitácora personal?",
      a: "Herramienta legal vital para registrar incidencias fuera del récord.",
      cat: "Práctica",
    },
    {
      q: "¿Canon 6?",
      a: "No atiendas pacientes si careces de dominio o destreza en la tarea.",
      cat: "Práctica",
    },
    {
      q: "¿Facultad BSN?",
      a: "Autonomía para establecer práctica privada e independiente.",
      cat: "Práctica",
    },
    {
      q: "¿Orden verbal?",
      a: "Solo en emergencias; debe ser firmada por el médico en 24 horas.",
      cat: "Práctica",
    },

    // SALARIOS (20+)
    {
      q: "¿Sueldo mínimo BSN?",
      a: "$3,000 con 1 año exp / $2,750 sin experiencia (Privado).",
      cat: "Salarios",
    },
    {
      q: "¿Prorrateo de sueldo?",
      a: "Si trabajas menos de 37.5h, el sueldo se calcula proporcionalmente.",
      cat: "Salarios",
    },
    {
      q: "¿Hora extra paga?",
      a: "Tiempo y medio ($1.5x) por cada hora tras las primeras 8h.",
      cat: "Salarios",
    },
    {
      q: "¿Bono de Navidad?",
      a: "Mandatorio si cumples las horas (1,350 o 700 según patrono).",
      cat: "Salarios",
    },
    {
      q: "¿Diferencial nocturno?",
      a: "Sujeto a convenio colectivo o política corporativa (No ley estatal).",
      cat: "Salarios",
    },
    {
      q: "¿Aumento por experiencia?",
      a: "Automático tras cumplir 1 año ejerciendo en la categoría licenciada.",
      cat: "Salarios",
    },
    {
      q: "¿Municipios pagan escala?",
      a: "Sí, el Reglamento 9425 les aplica obligatoriamente.",
      cat: "Salarios",
    },
    {
      q: "¿1099 cobra escala?",
      a: "Legalmente sí, pero patronos alegan que no son 'empleados'.",
      cat: "Salarios",
    },
    {
      q: "¿Tiempo para reclamar?",
      a: "Un año desde la violación salarial o cese del empleo.",
      cat: "Salarios",
    },
    {
      q: "¿Lunch pago?",
      a: "Solo si se labora por orden patronal; cuenta como tiempo extra.",
      cat: "Salarios",
    },
    {
      q: "¿Flexitime?",
      a: "Acuerdo de 4 días x 10h sin pago de extra diario (Sujeto a Ley 4).",
      cat: "Salarios",
    },
    {
      q: "¿Revisión de escalas?",
      a: "Cada 5 años por el Secretario de Salud según Ley 137.",
      cat: "Salarios",
    },
    {
      q: "¿Convenio colectivo?",
      a: "Si los salarios del convenio son menores a la ley, la ley prevalece.",
      cat: "Salarios",
    },
    {
      q: "¿Exenciones Ley 137?",
      a: "Solo patronos con un único enfermero empleado.",
      cat: "Salarios",
    },
    {
      q: "¿OATRH?",
      a: "Agencia que fiscaliza el cumplimiento de escalas en el gobierno.",
      cat: "Salarios",
    },
    {
      q: "¿Retención 10%?",
      a: "Obligatoria para contratos de servicios profesionales 1099.",
      cat: "Salarios",
    },
    {
      q: "¿Deducciones nómina?",
      a: "Solo las autorizadas por ley (Seguro Social, Medicare, Hacienda).",
      cat: "Salarios",
    },
    {
      q: "¿Sueldo ADN?",
      a: "$2,500 con experiencia / $2,250 sin experiencia.",
      cat: "Salarios",
    },
    {
      q: "¿Sueldo LPN?",
      a: "$2,000 con experiencia / $1,750 sin experiencia.",
      cat: "Salarios",
    },
    {
      q: "¿Reclamo Dept. Trabajo?",
      a: "Foro administrativo para cobrar sueldos no pagados.",
      cat: "Salarios",
    },

    // BENEFICIOS (20+)
    {
      q: "¿Regla de 130 horas?",
      a: "Mínimo mensual trabajado para acumular licencias (Ley 4-2017).",
      cat: "Beneficios",
    },
    {
      q: "¿Días de enfermedad?",
      a: "Un día por cada mes que labores al menos 130 horas.",
      cat: "Beneficios",
    },
    {
      q: "¿Vacaciones iniciales?",
      a: "Medio día (0.5) al mes durante el primer año.",
      cat: "Beneficios",
    },
    {
      q: "¿Vacaciones 15 años+?",
      a: "1.25 días al mes (15 anuales).",
      cat: "Beneficios",
    },
    {
      q: "¿Ley 60-2018?",
      a: "Permite usar 5 días de enfermedad para cuidar familiares directos.",
      cat: "Beneficios",
    },
    {
      q: "¿Pago de vacaciones?",
      a: "Obligatorio liquidarlas al renunciar o ser despedido.",
      cat: "Beneficios",
    },
    {
      q: "¿Enfermedad se paga?",
      a: "No se liquida en dinero al terminar empleo, se pierde el balance.",
      cat: "Beneficios",
    },
    {
      q: "¿Descanso entre turnos?",
      a: "Mínimo 8 horas consecutivas por ley de flexibilidad laboral.",
      cat: "Beneficios",
    },
    {
      q: "¿Acomodo religioso?",
      a: "Derecho a practicar fe sin peso oneroso al patrono.",
      cat: "Beneficios",
    },
    {
      q: "¿Licencia emergencia?",
      a: "5 días paga durante epidemias o emergencias decretadas.",
      cat: "Beneficios",
    },
    {
      q: "¿FMLA requisitos?",
      a: "Llevar un año en el empleo y haber laborado 1,250 horas.",
      cat: "Beneficios",
    },
    {
      q: "¿SINOT paga?",
      a: "Pago parcial por enfermedad no ocupacional vía Dept. Trabajo.",
      cat: "Beneficios",
    },
    {
      q: "¿Fondo del Seguro?",
      a: "Cubre gastos médicos y sueldo por accidentes en el hospital.",
      cat: "Beneficios",
    },
    {
      q: "¿Servicio Jurado?",
      a: "Licencia con paga completa por el tiempo que dure el servicio.",
      cat: "Beneficios",
    },
    {
      q: "¿Licencia Paternidad?",
      a: "Sujeto a FMLA o convenio colectivo (8 semanas en gobierno).",
      cat: "Beneficios",
    },
    {
      q: "¿Duelo familiar?",
      a: "Sujeto a política del patrono (Usualmente 3 días laborables).",
      cat: "Beneficios",
    },
    {
      q: "¿Exceso vacaciones?",
      a: "Puedes cobrar el exceso de 10 días por acuerdo escrito.",
      cat: "Beneficios",
    },
    {
      q: "¿Enfermedad acumulada?",
      a: "Arrastra hasta 15 días al año siguiente.",
      cat: "Beneficios",
    },
    {
      q: "¿Faltar por estudios?",
      a: "Derecho a solicitar horario flexible si es para grado académico.",
      cat: "Beneficios",
    },
    {
      q: "¿Reserva de puesto?",
      a: "Derecho a volver a tu plaza tras licencia médica o FMLA.",
      cat: "Beneficios",
    },

    // DESPIDO (20+)
    {
      q: "¿Mesada Ley 80?",
      a: "Indemnización de 3 meses + 2 semanas/año si no hay causa.",
      cat: "Despido",
    },
    {
      q: "¿Despido constructivo?",
      a: "Cuando condiciones hostiles te fuerzan a renunciar (cuenta como despido).",
      cat: "Despido",
    },
    {
      q: "¿Probatoria laboral?",
      a: "9 meses para contratos nuevos después de la Reforma 2017.",
      cat: "Despido",
    },
    {
      q: "¿Ley 115 represalia?",
      a: "Prohibido castigar al enfermero que denuncia faltas de seguridad.",
      cat: "Despido",
    },
    {
      q: "¿Causa por cierre?",
      a: "Es justa causa, pero deben retener al empleado más antiguo.",
      cat: "Despido",
    },
    {
      q: "¿Daños dobles?",
      a: "Penalidad al patrono bajo Ley 115 si hay represalia probada.",
      cat: "Despido",
    },
    {
      q: "¿Insubordinación causa?",
      a: "Sí, salvo que la orden sea ilegal o ponga en riesgo al paciente.",
      cat: "Despido",
    },
    {
      q: "¿Despido embarazada?",
      a: "Ilegal sin justa causa real y previa notificación a la UAD.",
      cat: "Despido",
    },
    {
      q: "¿Abuso de confianza?",
      a: "Causa para despido fulminante sin derecho a mesada.",
      cat: "Despido",
    },
    {
      q: "¿Nexo causal?",
      a: "Demostrar que el despido ocurrió por tu denuncia de irregularidades.",
      cat: "Despido",
    },
    {
      q: "¿Abogado honorarios?",
      a: "En casos de Ley 80, el tribunal asigna honorarios al patrono.",
      cat: "Despido",
    },
    {
      q: "¿Restitución de plaza?",
      a: "Derecho a recuperar tu empleo si el despido fue nulo.",
      cat: "Despido",
    },
    {
      q: "¿Justa causa real?",
      a: "Debe estar documentada por patrón de conducta o falta grave.",
      cat: "Despido",
    },
    {
      q: "¿Cambio turno oneroso?",
      a: "Táctica de despido constructivo para forzar tu renuncia.",
      cat: "Despido",
    },
    {
      q: "¿Evaluación falsa?",
      a: "Documentación fabricada para justificar un despido (Debe ser refutada).",
      cat: "Despido",
    },
    {
      q: "¿Mediación laboral?",
      a: "Vista informal obligatoria antes de ir a juicio en casos de Ley 80.",
      cat: "Despido",
    },
    {
      q: "¿Despido por enfermedad?",
      a: "Ilegal si tienes balance y el proceso fue justificado.",
      cat: "Despido",
    },
    {
      q: "¿LIFO en despidos?",
      a: "'Last In, First Out'. Antigüedad rige en cierres de unidad.",
      cat: "Despido",
    },
    {
      q: "¿Notificación escrita?",
      a: "El patrono debe entregarte carta detallando la causa del despido.",
      cat: "Despido",
    },
    {
      q: "¿Reclamo daños morales?",
      a: "Posible en casos de hostigamiento o represalias extremas.",
      cat: "Despido",
    },

    // MATERNIDAD (20+)
    {
      q: "¿8 semanas paga?",
      a: "Derecho a sueldo completo durante la licencia de maternidad.",
      cat: "Maternidad",
    },
    {
      q: "¿Ceder semanas padre?",
      a: "Hasta 4 semanas permitidas por la Ley 54-2023.",
      cat: "Maternidad",
    },
    {
      q: "¿Aborto licencia?",
      a: "Derecho a hasta 4 semanas paga para recuperación emocional/física.",
      cat: "Maternidad",
    },
    {
      q: "¿Hora de lactancia?",
      a: "Una hora diaria paga por un periodo de 12 meses.",
      cat: "Maternidad",
    },
    {
      q: "¿Certificado médico?",
      a: "Ya no es necesario presentarlo cada 4 meses (Ley 87-2025).",
      cat: "Maternidad",
    },
    {
      q: "¿Multa por lactancia?",
      a: "Mínimo $3,000 por infracción institucional.",
      cat: "Maternidad",
    },
    {
      q: "¿Sala de lactancia?",
      a: "Debe tener privacidad, silla, luz, ventilación y nevera.",
      cat: "Maternidad",
    },
    {
      q: "¿Lactar en baño?",
      a: "Prohibido por ley; el espacio debe ser clínico e higiénico.",
      cat: "Maternidad",
    },
    {
      q: "¿30 min parcial?",
      a: "Derecho por cada 4 horas trabajadas consecutivas.",
      cat: "Maternidad",
    },
    {
      q: "¿Bebé en NICU?",
      a: "El derecho a la extracción persiste para mantener la salud materna.",
      cat: "Maternidad",
    },
    {
      q: "¿Reserva de puesto?",
      a: "Garantía de volver a tu misma unidad y turno original.",
      cat: "Maternidad",
    },
    {
      q: "¿Extensión complicaciones?",
      a: "Hasta 12 semanas adicionales sin paga por certificado médico.",
      cat: "Maternidad",
    },
    {
      q: "¿Licencia adopción?",
      a: "Mismos derechos que parto biológico según la edad del menor.",
      cat: "Maternidad",
    },
    {
      q: "¿Hostigamiento retorno?",
      a: "Ilegal castigar o discriminar a la madre tras su reintegro.",
      cat: "Maternidad",
    },
    {
      q: "¿Interferencia turno?",
      a: "El supervisor debe asignar relevo para tu periodo de extracción.",
      cat: "Maternidad",
    },
    {
      q: "¿Plan de extracción?",
      a: "Acuerdo razonable de 2x30 o 3x20 minutos por turno.",
      cat: "Maternidad",
    },
    {
      q: "¿Exención hospital?",
      a: "Hospitales reciben beneficios en el CRIM por dar lactancia.",
      cat: "Maternidad",
    },
    {
      q: "¿Embarazo riesgo?",
      a: "Derecho a solicitar cambio de unidad si hay riesgo biológico.",
      cat: "Maternidad",
    },
    {
      q: "¿Evaluación desempeño?",
      a: "Ilegal bajar nota por ausencias de maternidad o lactancia.",
      cat: "Maternidad",
    },
    {
      q: "¿Unidad Antidiscrimen?",
      a: "Agencia (UAD) que atiende querellas de madres trabajadoras.",
      cat: "Maternidad",
    },

    // CONTRATOS (20+)
    {
      q: "¿Qué es At-Will?",
      a: "Empleo voluntario que el patrono puede terminar pagando mesada.",
      cat: "Contratos",
    },
    {
      q: "¿Cláusula no competencia?",
      a: "Nula si impide el sustento básico del enfermero asistencial.",
      cat: "Contratos",
    },
    {
      q: "¿Contrato verbal?",
      a: "Válido pero peligroso; siempre exige términos por escrito.",
      cat: "Contratos",
    },
    {
      q: "¿1099 y beneficios?",
      a: "No recibe vacaciones, enfermedad ni bono de Navidad.",
      cat: "Contratos",
    },
    {
      q: "¿W-2 protecciones?",
      a: "Subordinación total con protecciones de Reforma Laboral.",
      cat: "Contratos",
    },
    {
      q: "¿Secretos de negocio?",
      a: "Cláusulas válidas solo si manejas procesos administrativos únicos.",
      cat: "Contratos",
    },
    {
      q: "¿Renuncia sin aviso?",
      a: "Legal en PR, aunque se sugiere dar 15 días por cortesía.",
      cat: "Contratos",
    },
    {
      q: "¿Corporación Profesional?",
      a: "Estructura para enfermeros 1099 para proteger bienes personales.",
      cat: "Contratos",
    },
    {
      q: "¿Retención 10% Hacienda?",
      a: "Mandatoria en contratos de servicios profesionales.",
      cat: "Contratos",
    },
    {
      q: "¿Mal clasificación?",
      a: "Eres W-2 si el hospital controla tu horario, uniforme y equipo.",
      cat: "Contratos",
    },
    {
      q: "¿Buy-out agencias?",
      a: "Cláusula donde el hospital paga a la agencia por contratarte directo.",
      cat: "Contratos",
    },
    {
      q: "¿Deducciones 1099?",
      a: "Puedes deducir uniformes, equipo médico y educación en la planilla.",
      cat: "Contratos",
    },
    {
      q: "¿Garantía de horas?",
      a: "Asegura que tu contrato diga 'X horas mínimas' para evitar float.",
      cat: "Contratos",
    },
    {
      q: "¿Arbitraje obligatorio?",
      a: "Limita tu derecho a demandar en corte abierta; muy común hoy.",
      cat: "Contratos",
    },
    {
      q: "¿Enmiendas contrato?",
      a: "Solo son legales si ambas partes las firman por escrito.",
      cat: "Contratos",
    },
    {
      q: "¿Jurisdicción de ley?",
      a: "Debe decir 'Leyes del Estado Libre Asociado de PR'.",
      cat: "Contratos",
    },
    {
      q: "¿Terminación anticipada?",
      a: "Revisar penalidades económicas por irse antes de 1 año.",
      cat: "Contratos",
    },
    {
      q: "¿Uso de auto propio?",
      a: "Debe estar estipulado el millaje por reembolso en el contrato.",
      cat: "Contratos",
    },
    {
      q: "¿Seguro impericia 1099?",
      a: "Responsabilidad total del enfermero contratar su póliza propia.",
      cat: "Contratos",
    },
    {
      q: "¿Derecho a huelga?",
      a: "Protegido por ley federal (NLRA) si estás bajo unión.",
      cat: "Contratos",
    },

    // HOGARES (20+)
    {
      q: "¿Reglamento 9720?",
      a: "Normas críticas de enero 2026 para hogares de adultos mayores.",
      cat: "Hogares",
    },
    {
      q: "¿CPR presencial?",
      a: "Obligatorio; Familia no acepta certificaciones online.",
      cat: "Hogares",
    },
    {
      q: "¿Ratios 7AM-3PM?",
      a: "3 PSD para hogares con matrícula de 21 a 40 residentes.",
      cat: "Hogares",
    },
    {
      q: "¿Registro fármacos?",
      a: "Firma, dosis, fecha y hora real obligatoria en el MAR.",
      cat: "Hogares",
    },
    {
      q: "¿Restricción física?",
      a: "Máximo 24h con orden médica escrita específica.",
      cat: "Hogares",
    },
    {
      q: "¿Monitoreo restricción?",
      a: "Documentación visual de seguridad cada 15 minutos.",
      cat: "Hogares",
    },
    {
      q: "¿CPI presencial?",
      a: "Curso de intervención en crisis no violentas es mandatorio.",
      cat: "Hogares",
    },
    {
      q: "¿Negligencia institucional?",
      a: "Riesgo legal si el enfermero acepta ratios ilegales.",
      cat: "Hogares",
    },
    {
      q: "¿Ley 121?",
      a: "Carta de Derechos de la Persona de Edad Avanzada en PR.",
      cat: "Hogares",
    },
    {
      q: "¿Reporte maltrato?",
      a: "Mandatorio ante mínima sospecha razonable; penalidad si callas.",
      cat: "Hogares",
    },
    {
      q: "¿Intimidad residente?",
      a: "Derecho a privacidad física y de datos en todo procedimiento.",
      cat: "Hogares",
    },
    {
      q: "¿Hogar sustituto?",
      a: "Escenarios privados bajo fiscalización del Dept. Familia.",
      cat: "Hogares",
    },
    {
      q: "¿Administrador licencia?",
      a: "Requiere licencia vigente de Administrador de Hogares.",
      cat: "Hogares",
    },
    {
      q: "¿Fármacos PRN?",
      a: "No pueden darse por 'conveniencia' del staff; requieren orden MD.",
      cat: "Hogares",
    },
    {
      q: "¿Plan individualizado?",
      a: "Documento legal que guía todas tus intervenciones diarias.",
      cat: "Hogares",
    },
    {
      q: "¿Certificado gerontológico?",
      a: "Capacitación extra para personal de hogares (Reg. 9720).",
      cat: "Hogares",
    },
    {
      q: "¿LPN en hogares?",
      a: "Requiere RN disponible para consulta o supervisión periódica.",
      cat: "Hogares",
    },
    {
      q: "¿Seguridad entorno?",
      a: "Patrono responde por agresiones de pacientes en el hogar.",
      cat: "Hogares",
    },
    {
      q: "¿Insalubre rehusar?",
      a: "Derecho a no trabajar en hogares que arriesguen tu salud.",
      cat: "Hogares",
    },
    {
      q: "¿Fiscalización Familia?",
      a: "Visitas sorpresa para validar ratios y documentación clínica.",
      cat: "Hogares",
    },

    // GREMIO (20+)
    {
      q: "¿Cuota anual Colegio?",
      a: "Obligatoria para ejercer legalmente en PR bajo la Ley 82.",
      cat: "Gremio",
    },
    {
      q: "¿Seguro impericia?",
      a: "Cobertura básica incluida en la cuota del colegiado.",
      cat: "Gremio",
    },
    {
      q: "¿Asamblea General?",
      a: "Voto democrático por presupuestos, cuotas y reglamentos.",
      cat: "Gremio",
    },
    {
      q: "¿Representación Junta?",
      a: "Ayuda legal del Colegio en vistas administrativas éticas.",
      cat: "Gremio",
    },
    {
      q: "¿Unión vs Colegio?",
      a: "Unión negocia salarios; Colegio defiende la profesión y ética.",
      cat: "Gremio",
    },
    {
      q: "¿Educación gratis?",
      a: "Cursos de educación continua sin costo para colegiados.",
      cat: "Gremio",
    },
    {
      q: "¿Sello del Colegio?",
      a: "Validación de estatus activo para trámites oficiales.",
      cat: "Gremio",
    },
    {
      q: "¿Seguro de vida?",
      a: "Beneficio marginal incluido en la colegiación anual.",
      cat: "Gremio",
    },
    {
      q: "¿Fondos bienestar?",
      a: "Ayuda económica por enfermedad terminal o desastre para socios.",
      cat: "Gremio",
    },
    {
      q: "¿Miembro pasivo?",
      a: "Si no ejerces pero quieres mantener tu voz en el gremio.",
      cat: "Gremio",
    },
    {
      q: "¿Sedes regionales?",
      a: "Locales en toda la Isla para servicios al colegiado.",
      cat: "Gremio",
    },
    {
      q: "¿Vigilancia legislativa?",
      a: "El Colegio cabildea por leyes como el PS 687 (Ratios).",
      cat: "Gremio",
    },
    {
      q: "¿Revista científica?",
      a: "Medio oficial de divulgación de peritaje clínico local.",
      cat: "Gremio",
    },
    {
      q: "¿Carnet digital?",
      a: "Validación inmediata de estatus vía web o app del gremio.",
      cat: "Gremio",
    },
    {
      q: "¿Directiva Colegio?",
      a: "Cuerpos electos cada 2 o 3 años por los propios enfermeros.",
      cat: "Gremio",
    },
    {
      q: "¿Ley 82-1973?",
      a: "El estatuto que creó y da poder legal al Colegio.",
      cat: "Gremio",
    },
    {
      q: "¿Ética profesional?",
      a: "El Colegio vela por el cumplimiento del Reglamento 9650.",
      cat: "Gremio",
    },
    {
      q: "¿Denuncias patronos?",
      a: "El Colegio puede radicar interdictos por práctica ilegal.",
      cat: "Gremio",
    },
    {
      q: "¿Ayuda endoso?",
      a: "Validación de licencias para procesos migratorios a EE.UU.",
      cat: "Gremio",
    },
    {
      q: "¿Comité de pares?",
      a: "Apoyo en casos de adicción o problemas emocionales graves.",
      cat: "Gremio",
    },

    // FUTURO (20+)
    {
      q: "¿Impacto de la IA?",
      a: "Reducción de carga administrativa vía telemetría avanzada.",
      cat: "Futuro",
    },
    {
      q: "¿Incentivos Ley 60?",
      a: "Créditos contributivos proyectados por servicio en áreas críticas.",
      cat: "Futuro",
    },
    {
      q: "¿PS 687 Ratios?",
      a: "Futura ley que estandarizará la carga de pacientes en PR.",
      cat: "Futuro",
    },
    {
      q: "¿Telesalud remota?",
      a: "Crecimiento exponencial de roles de consultoría virtual.",
      cat: "Futuro",
    },
    {
      q: "¿Robótica salud?",
      a: "Uso de exoesqueletos para asistir en mecánica corporal.",
      cat: "Futuro",
    },
    {
      q: "¿Nurse Practitioner PR?",
      a: "Mayor autonomía diagnóstica en centros rurales y primarios.",
      cat: "Futuro",
    },
    {
      q: "¿Estándar global?",
      a: "Validación de destrezas para competir en el mercado externo.",
      cat: "Futuro",
    },
    {
      q: "¿Gestión de datos?",
      a: "Nuevo rol de analista de récords médicos electrónicos.",
      cat: "Futuro",
    },
    {
      q: "¿Salud mental leyes?",
      a: "Nuevas regulaciones de bienestar emocional para enfermeros.",
      cat: "Futuro",
    },
    {
      q: "¿Compact movilidad?",
      a: "Debate legislativo sobre licencia multi-estado en la Isla.",
      cat: "Futuro",
    },
    {
      q: "¿Nurse Entrepreneur?",
      a: "Aumento de agencias boutique lideradas por BSNs.",
      cat: "Futuro",
    },
    {
      q: "¿Educación VR?",
      a: "Simulaciones virtuales inmersivas para educación continua.",
      cat: "Futuro",
    },
    {
      q: "¿Medicina genómica?",
      a: "Nuevos roles de asesoría genética básica en enfermería.",
      cat: "Futuro",
    },
    {
      q: "¿Ciberseguridad?",
      a: "Mayor responsabilidad legal en protección HIPAA digital.",
      cat: "Futuro",
    },
    {
      q: "¿Modelos híbridos?",
      a: "Visita domiciliaria apoyada por monitoreo IoT constante.",
      cat: "Futuro",
    },
    {
      q: "¿Ayuda humanitaria?",
      a: "Certificaciones para brigadas de respuesta global.",
      cat: "Futuro",
    },
    {
      q: "¿Perito legal?",
      a: "Crecimiento del consultor de enfermería en tribunales.",
      cat: "Futuro",
    },
    {
      q: "¿Retención talento?",
      a: "Estrategias estatales de perdón de deuda estudiantil.",
      cat: "Futuro",
    },
    {
      q: "¿Sueldos EE.UU.?",
      a: "Presión para igualar salarios del continente para 2030.",
      cat: "Futuro",
    },
    {
      q: "¿Bioética avanzada?",
      a: "Nuevos retos en el manejo de tecnología y fin de vida.",
      cat: "Futuro",
    },
  ];

  const coachRecommendations = [
    {
      id: 1,
      title: "Vigilancia al PS 687",
      icon: TrendingUp,
      color: "amber",
      text: "Lucha por ratios fijos de pacientes.",
      recommendation:
        "Mantente activo en las asambleas del Colegio. Tu carga de trabajo es un asunto legal, no opcional.",
    },
    {
      id: 2,
      title: "Certificación Telesalud",
      icon: Monitor,
      color: "blue",
      text: "Nicho de empleo desde casa.",
      recommendation:
        "Toma las 4h de educación específica del Reg. 9518 y certifícate. Menos desgaste físico, más tecnología.",
    },
    {
      id: 3,
      title: "Bitácora Profesional",
      icon: Brain,
      color: "pink",
      text: "Tu escudo contra demandas.",
      recommendation:
        "Anota incidencias, órdenes inseguras y falta de personal con fecha y hora. Este documento es oro puro.",
    },
  ];

  const linksData = [
    {
      category: "Leyes de Práctica y Junta",
      links: [
        {
          name: "Ley 254-2015 (Ley de Enfermería)",
          url: "https://www.lexjuris.com/lexlex/leyes2015/lexl2015254.htm",
        },
        {
          name: "Reglamento 9104 (Práctica JEEPR)",
          url: "http://app.estado.gobierno.pr/ReglamentosOnLine/Reglamentos/9104.pdf",
        },
        {
          name: "Reglamento 9720 (Hogares Adultos Mayores)",
          url: "http://app.estado.gobierno.pr/ReglamentosOnLine/Reglamentos/9720.pdf",
        },
        {
          name: "Resolución 2024-159 (Dengue e Impericia)",
          url: "https://www.salud.pr.gov/CMS/DOWNLOAD/9188",
        },
        {
          name: "Reglamento 9651 (Educación Continua)",
          url: "https://www.salud.pr.gov/CMS/DOWNLOAD/9645",
        },
      ],
    },
    {
      category: "Referencias Rápidas Extra",
      links: [
        {
          name: "Proyecto del Senado 687 (Estatus Ratios)",
          url: "https://senado.pr.gov/ps687",
        },
        {
          name: "Clínica de Derecho Laboral UPR",
          url: "https://derecho.uprrp.edu/clinica-asistencia-legal/",
        },
        {
          name: "Guía 1040-PR (Seguro Social 1099)",
          url: "https://www.irs.gov/es/1040pr",
        },
      ],
    },
    {
      category: "Salarios y Beneficios",
      links: [
        {
          name: "Ley 137-2020 (Sector Privado)",
          url: "https://bvirtualogp.pr.gov/ogp/Bvirtual/leyesreferencia/PDF/27-2005.pdf",
        },
        {
          name: "Ley 136-2020 (Sector Público)",
          url: "https://www.lexjuris.com/lexlex/leyes2020/lexl2020136.htm",
        },
        {
          name: "Reglamento 9425 (Escalas Sector Público)",
          url: "https://www.salud.pr.gov/CMS/DOWNLOAD/6516",
        },
      ],
    },
  ];

  const filteredFaqs = useMemo(() => {
    return faqData.filter((item) => {
      const matchesSearch =
        item.q.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.a.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory =
        faqCategory === "Todos" || item.cat === faqCategory;
      return matchesSearch && matchesCategory;
    });
  }, [searchQuery, faqCategory]);

  const defenseSteps = [
    {
      title: "Fase 1: Silent Mode (Recopilación)",
      icon: Search,
      desc: "Guarda tus evidencias fuera de la red del hospital.",
      list: [
        "Copia de Roster",
        "Talonarios de pago",
        "Evaluaciones",
        "Mensajes guardados",
      ],
    },
    {
      title: "Fase 2: Paper Trail (Comunicación)",
      icon: FileText,
      desc: "Envía email formal citando leyes.",
      list: [
        "Cita Ley 254 (Alcance)",
        "Cita Reg. 9650 (Ética)",
        "Usa: 'Riesgo a seguridad de paciente'",
      ],
    },
    {
      title: "Fase 3: Chaleco Antibalas (Ley 115)",
      icon: Shield,
      desc: "Radica querella formal.",
      list: ["DTRH o PR OSHA", "Notifica al patrono", "Derecho a daños dobles"],
    },
  ];

  // --- Components ---
  const SectionHeader = ({ icon: Icon, title, subtitle }) => (
    <div className="mb-8">
      <div className="flex items-center gap-3 text-teal-600 mb-2">
        <Icon size={32} strokeWidth={2.5} />
        <h2 className="text-3xl font-black tracking-tight text-slate-800 uppercase">
          {title}
        </h2>
      </div>
      <p className="text-slate-500 font-medium ml-11 tracking-tight">
        {subtitle}
      </p>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 font-sans selection:bg-teal-100 pb-20 lg:pb-0 flex flex-col">
      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar */}
        <aside className="hidden lg:flex w-72 bg-slate-900 text-white flex-col p-6 shadow-2xl shrink-0">
          <div
            className="flex items-center gap-3 mb-10 px-2 cursor-pointer"
            onClick={() => setActiveSection("dashboard")}
          >
            <div className="bg-teal-500 p-2 rounded-xl shadow-lg">
              <Scale className="text-white" size={24} />
            </div>
            <div>
              <h1 className="font-black text-xl leading-none tracking-tighter text-white">
                Nurse Legal Coach
              </h1>
              <span className="text-[10px] text-teal-400 font-bold tracking-widest uppercase">
                Puerto Rico 2026
              </span>
            </div>
          </div>

          <nav className="space-y-1 overflow-y-auto custom-scrollbar pr-2">
            {[
              { id: "dashboard", icon: Info, label: "Resumen 2026" },
              { id: "leyes", icon: Library, label: "Compendio Legal" },
              { id: "search", icon: Search, label: "Centro de Respuestas" },
              { id: "alcance", icon: Stethoscope, label: "Alcance Detallado" },
              { id: "economia", icon: DollarSign, label: "Salarios Ley 137" },
              {
                id: "beneficios",
                icon: Umbrella,
                label: "Beneficios y Licencias",
              },
              { id: "despidos", icon: Gavel, label: "Protección Despido" },
              { id: "practica", icon: Zap, label: "Práctica y Ética" },
              { id: "maternidad", icon: Baby, label: "Maternidad / Lactancia" },
              { id: "coach", icon: Lightbulb, label: "Tips del Coach" },
              { id: "biblioteca", icon: BookOpen, label: "Biblioteca PDF" },
            ].map((item) => (
              <button
                key={item.id}
                onClick={() => setActiveSection(item.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all font-bold text-sm ${
                  activeSection === item.id
                    ? "bg-teal-600 text-white shadow-lg shadow-teal-600/30"
                    : "text-slate-400 hover:bg-slate-800 hover:text-white"
                }`}
              >
                <item.icon size={18} />
                {item.label}
              </button>
            ))}
          </nav>
        </aside>

        {/* Content Area */}
        <main className="flex-1 overflow-y-auto p-4 md:p-8 lg:p-12 scroll-smooth">
          {activeSection === "dashboard" && (
            <div className="max-w-4xl mx-auto space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
              <div className="bg-gradient-to-br from-teal-600 to-indigo-700 rounded-[2.5rem] p-8 md:p-12 text-white shadow-2xl relative overflow-hidden">
                <div className="relative z-10">
                  <span className="bg-white/20 backdrop-blur-md px-4 py-1 rounded-full text-xs font-black uppercase tracking-widest mb-6 inline-block">
                    Edición 2026
                  </span>
                  <h1 className="text-4xl md:text-6xl font-black leading-tight mb-4 uppercase tracking-tighter">
                    Manual de Poder <br />
                    para la Enfermería
                  </h1>
                  <p className="text-teal-50 text-xl md:text-2xl font-bold opacity-90 border-l-4 border-teal-400 pl-4 mb-8">
                    Herramienta Legal para Enfermeros en PR
                  </p>
                  <div className="flex flex-wrap gap-4">
                    <button
                      onClick={() => setActiveSection("search")}
                      className="bg-white text-teal-700 font-black px-8 py-4 rounded-2xl flex items-center gap-3 hover:scale-105 transition-transform shadow-xl"
                    >
                      <Search size={20} /> Empezar Búsqueda
                    </button>
                    <button
                      onClick={() => setActiveSection("leyes")}
                      className="bg-teal-500/20 backdrop-blur-md text-white border border-white/30 font-black px-8 py-4 rounded-2xl flex items-center gap-3 hover:bg-white/10 transition-all shadow-xl"
                    >
                      <Library size={20} /> Compendio Legal
                    </button>
                  </div>
                </div>
                <div className="absolute top-[-10%] right-[-10%] opacity-10 shadow-inner text-white">
                  <Shield size={400} />
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-4 text-slate-800">
                <div
                  onClick={() => setActiveSection("alcance")}
                  className="cursor-pointer bg-white p-6 rounded-3xl shadow-sm border border-slate-100 flex flex-col gap-3 hover:border-teal-500 transition-all group"
                >
                  <div className="bg-teal-50 text-teal-600 w-12 h-12 rounded-2xl flex items-center justify-center font-black group-hover:bg-teal-600 group-hover:text-white transition-colors">
                    SCOPE
                  </div>
                  <h4 className="font-bold">Alcance Legal</h4>
                  <p className="text-sm text-slate-500">
                    Funciones por categoría.
                  </p>
                </div>
                <div
                  onClick={() => setActiveSection("search")}
                  className="cursor-pointer bg-white p-6 rounded-3xl shadow-sm border border-slate-100 flex flex-col gap-3 hover:border-amber-500 transition-all group"
                >
                  <div className="bg-amber-50 text-amber-600 w-12 h-12 rounded-2xl flex items-center justify-center font-black group-hover:bg-amber-600 group-hover:text-white transition-colors">
                    FAQ
                  </div>
                  <h4 className="font-bold">Respuestas</h4>
                  <p className="text-sm text-slate-500">
                    Más de 200 puntos de ley.
                  </p>
                </div>
                <div
                  onClick={() => setActiveSection("despidos")}
                  className="cursor-pointer bg-white p-6 rounded-3xl shadow-sm border border-slate-100 flex flex-col gap-3 hover:border-indigo-500 transition-all group"
                >
                  <div className="bg-indigo-50 text-indigo-600 w-12 h-12 rounded-2xl flex items-center justify-center font-black group-hover:bg-indigo-600 group-hover:text-white transition-colors">
                    DEF
                  </div>
                  <h4 className="font-bold">Protección</h4>
                  <p className="text-sm text-slate-500">
                    Defensa ante despidos.
                  </p>
                </div>
              </div>
            </div>
          )}

          {activeSection === "search" && (
            <div className="max-w-4xl mx-auto space-y-6 animate-in fade-in duration-500">
              <SectionHeader
                icon={HelpCircle}
                title="Centro de Respuestas"
                subtitle="Base de datos legal completa de enfermería en PR"
              />
              <div className="sticky top-0 z-20 bg-slate-50/90 backdrop-blur-md pb-4 space-y-4 text-slate-800">
                <div className="relative">
                  <Search
                    className="absolute left-6 top-1/2 -translate-y-1/2 text-slate-400"
                    size={24}
                  />
                  <input
                    type="text"
                    placeholder="Busca por 'maternidad', 'hogares', 'despido', 'IA'..."
                    className="w-full bg-white border-2 border-slate-100 rounded-[2rem] pl-16 pr-8 py-5 text-lg font-bold shadow-sm focus:border-teal-500 focus:ring-0 transition-all"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery("")}
                      className="absolute right-6 top-1/2 -translate-y-1/2 bg-slate-100 p-1 rounded-full hover:bg-slate-200"
                    >
                      <X size={16} />
                    </button>
                  )}
                </div>
                <div className="flex gap-2 overflow-x-auto pb-2 no-scrollbar">
                  {[
                    "Todos",
                    "Licencia",
                    "Práctica",
                    "Salarios",
                    "Beneficios",
                    "Maternidad",
                    "Despido",
                    "Contratos",
                    "Hogares",
                    "Gremio",
                    "Futuro",
                  ].map((cat) => (
                    <button
                      key={cat}
                      onClick={() => setFaqCategory(cat)}
                      className={`px-6 py-2 rounded-full text-xs font-black uppercase border-2 transition-all ${
                        faqCategory === cat
                          ? "bg-teal-600 border-teal-600 text-white shadow-lg"
                          : "bg-white border-slate-100 text-slate-500 hover:border-teal-200"
                      }`}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid gap-4 pb-20">
                {filteredFaqs.length > 0 ? (
                  filteredFaqs.map((faq, idx) => (
                    <div
                      key={idx}
                      className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm hover:shadow-md transition-all group"
                    >
                      <div className="flex justify-between items-center mb-2">
                        <span className="bg-slate-100 text-slate-500 text-[10px] font-black uppercase px-3 py-1 rounded-full">
                          {faq.cat}
                        </span>
                        <ChevronDown className="text-slate-200" size={16} />
                      </div>
                      <h4 className="text-lg font-black text-slate-800 mb-3 tracking-tighter leading-tight mt-2">
                        {faq.q}
                      </h4>
                      <p className="text-slate-700 font-bold leading-relaxed bg-slate-50 p-4 rounded-2xl border-l-4 border-teal-500">
                        {faq.a}
                      </p>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-20 opacity-50">
                    <Search size={48} className="mx-auto mb-4 text-slate-400" />
                    <p className="font-bold text-slate-400">
                      No encontramos resultados para esa búsqueda.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeSection === "leyes" && (
            <div className="max-w-4xl mx-auto space-y-6 animate-in fade-in duration-500">
              <SectionHeader
                icon={Library}
                title="Compendio Legal"
                subtitle="Las 23 leyes que rigen tu práctica profesional"
              />
              <div className="grid gap-6">
                {lawsCompendium.map((law) => (
                  <div
                    key={law.id}
                    className="bg-white p-8 rounded-[2rem] shadow-sm border border-slate-100 group hover:shadow-md transition-all text-slate-800"
                  >
                    <div className="flex justify-between gap-4 mb-4">
                      <div>
                        <h3 className="text-2xl font-black tracking-tighter">
                          {law.title}
                        </h3>
                        <p className="text-teal-600 font-bold uppercase text-[10px] tracking-widest">
                          {law.subtitle}
                        </p>
                      </div>
                      <div className="bg-slate-900 text-teal-400 px-3 py-1 rounded-lg text-xs font-black h-fit">
                        ID #{law.id}
                      </div>
                    </div>
                    <p className="text-slate-600 font-medium mb-4">
                      {law.desc}
                    </p>
                    <div className="bg-slate-50 p-4 rounded-2xl border-l-4 border-slate-900 text-sm font-bold">
                      <span className="text-slate-400 uppercase text-[10px] block mb-1 tracking-widest">
                        Impacto para el Enfermero:
                      </span>
                      {law.impact}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeSection === "alcance" && (
            <div className="max-w-4xl mx-auto space-y-8 animate-in fade-in duration-500">
              <SectionHeader
                icon={Stethoscope}
                title="Alcance Legal Detallado"
                subtitle="Ley de Enfermería 254-2015"
              />
              <div className="grid gap-8">
                {Object.entries(nursingCategories).map(([key, cat]) => (
                  <div
                    key={key}
                    className="bg-white rounded-[2.5rem] p-8 md:p-12 shadow-sm border border-slate-100 group text-slate-800"
                  >
                    <div className="flex items-center gap-4 mb-8">
                      <div className="bg-slate-900 text-teal-400 w-16 h-16 rounded-[1.5rem] flex items-center justify-center font-black text-2xl shadow-xl">
                        {key}
                      </div>
                      <div>
                        <h3 className="text-3xl font-black tracking-tighter">
                          {cat.title}
                        </h3>
                        <p className="text-teal-600 font-bold uppercase text-xs tracking-widest">
                          Estatus Jurídico Ley 254
                        </p>
                      </div>
                    </div>
                    <div className="bg-slate-50 p-6 rounded-3xl mb-8 border-l-8 border-teal-500 shadow-sm">
                      <p className="text-slate-700 font-medium italic leading-relaxed">
                        "{cat.scope}"
                      </p>
                    </div>
                    <div className="grid md:grid-cols-2 gap-8">
                      <div className="space-y-4">
                        <h4 className="font-black text-slate-900 uppercase text-xs tracking-widest flex items-center gap-2">
                          Permitido por Ley:
                        </h4>
                        <ul className="space-y-3">
                          {cat.details.map((detail, i) => (
                            <li
                              key={i}
                              className="flex gap-3 items-start text-sm font-bold text-slate-600"
                            >
                              <ChevronRight
                                size={14}
                                className="text-teal-500 mt-1 shrink-0"
                              />{" "}
                              {detail}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="space-y-4">
                        <h4 className="font-black text-red-600 uppercase text-xs tracking-widest flex items-center gap-2">
                          Advertencia Legal:
                        </h4>
                        <div className="bg-red-50 p-6 rounded-3xl border-2 border-dashed border-red-200">
                          <p className="text-red-900 text-sm font-bold leading-relaxed italic uppercase">
                            {cat.warning}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeSection === "economia" && (
            <div className="max-w-4xl mx-auto space-y-8 animate-in fade-in duration-500 text-slate-800">
              <SectionHeader
                icon={DollarSign}
                title="Economía Profesional"
                subtitle="Escalas Salariales Ley 137"
              />
              <div className="bg-white rounded-[2.5rem] p-8 shadow-sm border border-slate-100 relative overflow-hidden">
                <Calculator
                  className="absolute top-8 right-8 text-slate-50"
                  size={120}
                />
                <h3 className="text-2xl font-black text-slate-800 mb-6 tracking-tighter">
                  Calculadora Salarial
                </h3>
                <div className="grid md:grid-cols-2 gap-6 mb-8 text-slate-800 font-bold">
                  <div className="space-y-4">
                    <label className="block text-sm uppercase tracking-widest text-slate-500">
                      Categoría Licencia
                    </label>
                    <select
                      value={salaryConfig.category}
                      onChange={(e) =>
                        setSalaryConfig({
                          ...salaryConfig,
                          category: e.target.value,
                        })
                      }
                      className="w-full bg-slate-50 border-none rounded-2xl px-5 py-4 font-bold focus:ring-2 focus:ring-teal-500"
                    >
                      {Object.entries(nursingCategories).map(([k, v]) => (
                        <option key={k} value={k}>
                          {v.title}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-4">
                    <label className="block text-sm uppercase tracking-widest text-slate-500">
                      Experiencia (1 Año+)
                    </label>
                    <div className="flex gap-2">
                      {["yes", "no"].map((opt) => (
                        <button
                          key={opt}
                          onClick={() =>
                            setSalaryConfig({ ...salaryConfig, exp: opt })
                          }
                          className={`flex-1 py-4 rounded-2xl font-bold transition-all ${
                            salaryConfig.exp === opt
                              ? "bg-teal-600 text-white shadow-md"
                              : "bg-slate-50 text-slate-400 hover:bg-slate-100"
                          }`}
                        >
                          {opt === "yes" ? "Sí" : "No"}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="bg-slate-900 rounded-[2rem] p-8 text-center text-white shadow-xl">
                  <span className="text-teal-400 text-xs font-black uppercase tracking-[0.2em]">
                    Sueldo Mensual Mínimo de Ley
                  </span>
                  <div className="text-5xl md:text-7xl font-black mt-2 tracking-tighter">
                    $
                    {(salaryConfig.exp === "yes"
                      ? nursingCategories[salaryConfig.category].exp
                      : nursingCategories[salaryConfig.category].base
                    ).toLocaleString()}
                    .00
                  </div>
                  <p className="text-[10px] text-slate-500 mt-2 font-bold uppercase tracking-widest">
                    Basado en Ley 137 (Sector Privado) y Reg. 9425 (Sector
                    Público)
                  </p>
                </div>
              </div>
            </div>
          )}

          {activeSection === "beneficios" && (
            <div className="max-w-4xl mx-auto space-y-8 animate-in fade-in duration-500 text-slate-800">
              <SectionHeader
                icon={Umbrella}
                title="Beneficios y Licencias"
                subtitle="Acumulación bajo la Reforma Laboral 2017"
              />
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-white p-8 rounded-[2rem] border border-slate-100 shadow-sm">
                  <h3 className="text-xl font-black mb-4 flex items-center gap-2 text-indigo-600">
                    <Clock /> Regla de las 130 Horas
                  </h3>
                  <p className="text-slate-600 text-sm font-medium mb-6 leading-relaxed">
                    Para acumular vacaciones y enfermedad, debes laborar al
                    menos 130 horas en el mes calendario (Ley 4-2017).
                  </p>
                  <div className="bg-slate-50 p-6 rounded-2xl text-center">
                    <input
                      type="range"
                      min="0"
                      max="160"
                      value={benefitHours}
                      onChange={(e) => setBenefitHours(e.target.value)}
                      className="w-full accent-indigo-600 mb-4"
                    />
                    <p className="text-2xl font-black">
                      {benefitHours}{" "}
                      <span className="text-xs text-slate-400 uppercase">
                        Horas
                      </span>
                    </p>
                    <span
                      className={`mt-2 inline-block px-4 py-1 rounded-full text-[10px] font-black uppercase ${
                        benefitHours >= 130
                          ? "bg-green-100 text-green-700"
                          : "bg-red-100 text-red-700"
                      }`}
                    >
                      {benefitHours >= 130
                        ? "Estatus: Acumulas"
                        : "Estatus: No Acumulas"}
                    </span>
                  </div>
                </div>
                <div className="bg-slate-900 text-white p-8 rounded-[2rem] shadow-xl relative overflow-hidden">
                  <HandHelping
                    className="absolute -right-8 -bottom-8 opacity-10"
                    size={160}
                  />
                  <h3 className="text-xl font-black mb-4 flex items-center gap-2 text-teal-400">
                    <Heart /> Licencias Especiales
                  </h3>
                  <div className="space-y-4 text-xs font-bold text-slate-300 uppercase tracking-wider">
                    <div className="flex justify-between border-b border-slate-700 pb-2">
                      <span>FMLA (Federal)</span>
                      <span>12 Semanas s/paga</span>
                    </div>
                    <div className="flex justify-between border-b border-slate-700 pb-2">
                      <span>SINOT (Enfermedad)</span>
                      <span>Pago parcial Dept. Trabajo</span>
                    </div>
                    <div className="flex justify-between border-b border-slate-700 pb-2">
                      <span>Ley 60 (Familiares)</span>
                      <span>5 días de tu balance</span>
                    </div>
                    <div className="flex justify-between border-b border-slate-700 pb-2">
                      <span>Servicio Jurado</span>
                      <span>Paga completa</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Duelo</span>
                      <span>3 a 5 días (según política)</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeSection === "despidos" && (
            <div className="max-w-4xl mx-auto space-y-8 animate-in fade-in duration-500 text-slate-800">
              <SectionHeader
                icon={Gavel}
                title="Protección ante Despidos"
                subtitle="Ley 80 (Mesada) y Ley 115 (Represalias)"
              />
              <div className="grid gap-6 text-slate-800">
                <div className="bg-white p-8 md:p-12 rounded-[2.5rem] border border-slate-100 shadow-sm">
                  <h3 className="text-2xl font-black mb-6 tracking-tighter flex items-center gap-2 text-red-600">
                    <FileWarning /> ¿Qué es Justa Causa?
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4 text-sm font-bold">
                    <div className="p-5 bg-slate-50 rounded-2xl border-l-4 border-indigo-500">
                      <p className="text-indigo-600 uppercase text-[10px] mb-1 tracking-widest">
                        Conducta:
                      </p>
                      <p className="text-slate-700 leading-tight">
                        Patrón de conducta impropia, violaciones éticas o
                        insubordinación.
                      </p>
                    </div>
                    <div className="p-5 bg-slate-50 rounded-2xl border-l-4 border-indigo-500">
                      <p className="text-indigo-600 uppercase text-[10px] mb-1 tracking-widest">
                        Ineficiencia:
                      </p>
                      <p className="text-slate-700 leading-tight">
                        Rendimiento pobre tras haber sido advertido por escrito.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="bg-red-900 text-white p-8 md:p-12 rounded-[2.5rem] shadow-2xl relative overflow-hidden">
                  <Shield
                    className="absolute -right-10 -bottom-10 opacity-10"
                    size={240}
                  />
                  <div className="relative z-10">
                    <h3 className="text-2xl font-black mb-4 uppercase tracking-widest">
                      Ley 115: El Chaleco Antibalas
                    </h3>
                    <p className="text-red-100 font-medium leading-relaxed mb-6">
                      Prohíbe al patrono despedir, amenazar o discriminar a un
                      enfermero por denunciar actos ilegales o inseguridad
                      clínica.
                    </p>
                    <div className="bg-white/10 p-6 rounded-2xl border border-white/20">
                      <p className="text-sm font-black mb-2">
                        Si radicas una querella formal:
                      </p>
                      <ul className="text-xs space-y-2 opacity-90 font-bold italic list-disc pl-4 text-white">
                        <li>Derecho a daños dobles por despido injusto.</li>
                        <li>Derecho a reinstalación inmediata en tu puesto.</li>
                        <li>
                          Presunción de represalia si el despido es cercano a la
                          denuncia.
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeSection === "practica" && (
            <div className="max-w-4xl mx-auto space-y-8 animate-in fade-in duration-500 text-slate-800">
              <SectionHeader
                icon={Zap}
                title="Práctica y Ética Clínica"
                subtitle="Escenarios críticos de responsabilidad profesional"
              />
              <div className="grid gap-6">
                <div className="bg-white p-8 rounded-[2.5rem] border border-slate-100 shadow-sm">
                  <h3 className="text-xl font-black mb-4 flex items-center gap-2 text-red-600">
                    <AlertCircle /> Abandono de Paciente
                  </h3>
                  <div className="p-6 bg-red-50 rounded-2xl border-2 border-dashed border-red-200">
                    <p className="text-sm font-bold text-red-900 mb-4 italic">
                      Escenario: "Tu relevo no llega y el supervisor te dice que
                      te puedes ir."
                    </p>
                    <p className="text-red-800 text-sm font-black leading-relaxed">
                      ¡NO TE VAYAS! Irte sin entrega formal es abandono y
                      conlleva revocación de licencia. Documenta que notificaste
                      y espera al RN cualificado.
                    </p>
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="bg-white p-8 rounded-[2rem] border-t-8 border-teal-600 shadow-sm">
                    <h3 className="font-black text-lg mb-2 uppercase tracking-tighter">
                      Canon 6: Peritaje
                    </h3>
                    <p className="text-slate-600 text-sm font-medium leading-relaxed">
                      No atiendas pacientes ni realices tareas si careces del
                      dominio y destreza necesaria. Tu licencia está en riesgo
                      ante impericia.
                    </p>
                  </div>
                  <div className="bg-white p-8 rounded-[2rem] border-t-8 border-indigo-600 shadow-sm">
                    <h3 className="font-black text-lg mb-2 uppercase tracking-tighter text-indigo-700">
                      Canon 13: Dignidad
                    </h3>
                    <p className="text-slate-600 text-sm font-medium leading-relaxed">
                      No aceptes tareas que vayan contra los estándares mínimos
                      de la profesión (como limpieza profunda o labores no
                      clínicas).
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeSection === "maternidad" && (
            <div className="max-w-4xl mx-auto space-y-8 animate-in fade-in duration-500 text-slate-800">
              <SectionHeader
                icon={Baby}
                title="Maternidad y Lactancia"
                subtitle="Protección Ley 87-2025"
              />
              <div className="bg-white p-8 rounded-[2.5rem] border border-pink-100 shadow-sm relative overflow-hidden group">
                <Heart
                  className="absolute -right-10 -bottom-10 text-pink-50 opacity-20 group-hover:scale-125 transition-transform text-pink-100"
                  size={300}
                />
                <div className="relative z-10">
                  <div className="flex items-center gap-3 mb-6">
                    <Clock className="text-pink-500" size={32} />
                    <h3 className="text-2xl font-black tracking-tighter uppercase">
                      1 Hora Diaria Paga
                    </h3>
                  </div>
                  <p className="text-slate-600 font-bold leading-relaxed mb-6">
                    Garantizado por 12 meses desde el regreso. Sin necesidad de
                    certificado médico recurrente según la nueva Ley 87-2025.
                  </p>
                  <div className="p-6 rounded-3xl bg-red-50 border-l-4 border-red-500 text-red-900 font-bold italic shadow-sm text-sm">
                    Penalidad al patrono: Mínimo $3,000 por infracción + Daños
                    Dobles por despido injustificado.
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeSection === "coach" && (
            <div className="max-w-4xl mx-auto space-y-6 animate-in fade-in duration-500 text-slate-800">
              <SectionHeader
                icon={Lightbulb}
                title="Tips del Coach"
                subtitle="Recomendaciones estratégicas para 2026"
              />
              <div className="grid gap-8">
                {coachRecommendations.map((tip) => (
                  <div
                    key={tip.id}
                    className="bg-white rounded-[2rem] p-8 shadow-sm border border-slate-100 relative group overflow-hidden"
                  >
                    <div className="flex items-center gap-4 mb-4">
                      <div
                        className={`p-3 rounded-2xl bg-${tip.color}-50 text-${tip.color}-600 shadow-sm`}
                      >
                        <tip.icon size={24} />
                      </div>
                      <h3 className="text-xl font-black text-slate-800 tracking-tighter uppercase">
                        {tip.title}
                      </h3>
                    </div>
                    <p className="text-slate-600 font-medium mb-6 leading-relaxed">
                      {tip.text}
                    </p>
                    <div
                      className={`p-6 rounded-2xl bg-${tip.color}-50 border-l-4 border-${tip.color}-500 flex gap-4 shadow-sm text-slate-800`}
                    >
                      <ArrowRight
                        className={`text-${tip.color}-600 shrink-0 mt-1`}
                        size={20}
                      />
                      <div>
                        <p
                          className={`text-${tip.color}-900 font-black text-xs uppercase mb-1 tracking-tighter`}
                        >
                          Recomendación Coach:
                        </p>
                        <p className="text-slate-700 text-sm font-bold leading-relaxed">
                          {tip.recommendation}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeSection === "biblioteca" && (
            <div className="max-w-4xl mx-auto space-y-8 animate-in fade-in duration-500 text-slate-800">
              <SectionHeader
                icon={BookOpen}
                title="Biblioteca Legal"
                subtitle="Lista Maestra de Referencias"
              />
              <div className="grid gap-8">
                {linksData.map((section, idx) => (
                  <div
                    key={idx}
                    className="bg-white rounded-[2rem] overflow-hidden border border-slate-100 shadow-sm"
                  >
                    <div className="bg-slate-900 text-white px-8 py-4 font-black uppercase text-sm tracking-widest text-white">
                      {section.category}
                    </div>
                    <div className="divide-y divide-slate-50">
                      {section.links.map((link, lIdx) => (
                        <a
                          key={lIdx}
                          href={link.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center justify-between p-6 hover:bg-slate-50 transition-colors group text-slate-800"
                        >
                          <div className="flex items-center gap-4">
                            <div className="bg-teal-50 p-2 rounded-lg text-teal-600 group-hover:bg-teal-600 group-hover:text-white transition-all">
                              <LinkIcon size={18} />
                            </div>
                            <span className="font-bold">{link.name}</span>
                          </div>
                          <ExternalLink
                            size={18}
                            className="text-slate-300 group-hover:text-teal-500"
                          />
                        </a>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeSection === "recursos" && (
            <div className="max-w-4xl mx-auto space-y-8 animate-in fade-in duration-500 text-slate-800">
              <SectionHeader
                icon={Phone}
                title="Ayuda Legal"
                subtitle="Directorio Pro-Bono PR"
              />
              <div className="grid md:grid-cols-2 gap-6">
                {[
                  {
                    name: "Servicios Legales PR",
                    phone: "787-728-5070",
                    area: "Toda la Isla",
                  },
                  {
                    name: "Pro-Bono, Inc.",
                    phone: "787-721-3398",
                    area: "Empleo y Discrimen",
                  },
                  {
                    name: "Clínica UPR Laboral",
                    phone: "787-999-9570",
                    area: "Salarios y Madres",
                  },
                ].map((contact, i) => (
                  <div
                    key={i}
                    className="bg-white p-6 rounded-3xl border border-slate-100 hover:border-teal-500 transition-all shadow-sm"
                  >
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1 text-slate-400">
                      {contact.area}
                    </p>
                    <h4 className="text-xl font-black text-slate-800 mb-4 tracking-tighter">
                      {contact.name}
                    </h4>
                    <a
                      href={`tel:${contact.phone.replace(/-/g, "")}`}
                      className="inline-flex items-center gap-2 font-black text-lg text-teal-600"
                    >
                      <Phone size={18} /> {contact.phone}
                    </a>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeSection === "junta" && (
            <div className="max-w-4xl mx-auto space-y-8 animate-in fade-in duration-500 text-slate-800">
              <SectionHeader
                icon={FileText}
                title="Regulación"
                subtitle="JEEPR vs CPEPR"
              />
              <div className="grid md:grid-cols-2 gap-8">
                <div className="bg-white p-8 rounded-[2rem] border-t-8 border-indigo-600 shadow-sm transition-all hover:scale-105">
                  <h3 className="text-xl font-black mb-2 tracking-tighter">
                    La Junta (JEEPR)
                  </h3>
                  <p className="text-sm font-bold text-slate-400 uppercase tracking-widest mb-4 text-slate-400 font-bold">
                    Regulador del Estado
                  </p>
                  <p className="text-slate-600 font-bold text-sm leading-relaxed mb-4">
                    Administra licencias y reválidas. Protege al paciente
                    fiscalizando tu cumplimiento ético y técnico.
                  </p>
                  <span className="text-[10px] bg-slate-100 px-2 py-1 rounded font-black text-slate-500 uppercase">
                    Fiscaliza al profesional
                  </span>
                </div>
                <div className="bg-white p-8 rounded-[2rem] border-t-8 border-teal-600 shadow-sm transition-all hover:scale-105">
                  <h3 className="text-xl font-black mb-2 tracking-tighter text-teal-700">
                    El Colegio (CPEPR)
                  </h3>
                  <p className="text-sm font-bold text-slate-400 uppercase tracking-widest mb-4 text-slate-400 font-bold">
                    Gremio Profesional
                  </p>
                  <p className="text-slate-600 font-bold text-sm leading-relaxed mb-4">
                    Defiende tus derechos económicos y gremiales. La colegiación
                    es compulsoria por Ley 82.
                  </p>
                  <span className="text-[10px] bg-slate-100 px-2 py-1 rounded font-black text-slate-500 uppercase">
                    Defiende al profesional
                  </span>
                </div>
              </div>
            </div>
          )}
        </main>
      </div>

      {/* Mobile Navigation */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-xl border-t border-slate-200 z-50 px-2 py-3 flex justify-around items-center shadow-2xl overflow-x-auto no-scrollbar">
        {[
          { id: "dashboard", icon: Info, label: "Inicio" },
          { id: "search", icon: Search, label: "Respuestas" },
          { id: "leyes", icon: Library, label: "Leyes" },
          { id: "alcance", icon: Stethoscope, label: "Scope" },
          { id: "coach", icon: Lightbulb, label: "Coach" },
        ].map((item) => (
          <button
            key={item.id}
            onClick={() => setActiveSection(item.id)}
            className={`flex flex-col items-center gap-1 transition-all px-3 shrink-0 ${
              activeSection === item.id
                ? "text-teal-600 scale-110"
                : "text-slate-400"
            }`}
          >
            <item.icon
              size={20}
              strokeWidth={activeSection === item.id ? 2.5 : 2}
            />
            <span className="text-[9px] font-black uppercase tracking-tighter">
              {item.label}
            </span>
          </button>
        ))}
      </nav>
    </div>
  );
};

export default App;
